import { CustomSelect, SearchInput } from '@/components';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, SettingsIcon } from './icons';
import addSquareIcon from '../../assets/add-square.png';
import {
  formatDayTitle,
  formatMonthTitle,
  formatWeekTitle,
  APPOINTMENT_STATUSES,
  STATUS_META,
} from '../utils';

const VIEWS = [
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  ...APPOINTMENT_STATUSES.map((s) => ({
    value: s,
    label: STATUS_META[s].label,
  })),
];

export function CalendarToolbar({
  view,
  onViewChange,
  anchorKey,
  onPrev,
  onNext,
  statusFilter,
  onStatusFilterChange,
  canCreate,
  canViewSettings,
  onNewAppointment,
  searchQuery,
  onSearchQueryChange,
}) {
  const navigate = useNavigate();
  const title =
    view === 'month'
      ? formatMonthTitle(anchorKey)
      : view === 'week'
        ? formatWeekTitle(anchorKey)
        : formatDayTitle(anchorKey);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 w-full">
      {/* Left: Navigation and Title */}
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={onPrev}
          className="text-[#50AB3D] hover:text-[#3d832e] transition-colors"
          aria-label="Previous"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>

        <h2 className="text-xl font-semibold text-[#1F2241] whitespace-nowrap">
          {title}
        </h2>

        <button
          type="button"
          onClick={onNext}
          className="text-[#50AB3D] hover:text-[#3d832e] transition-colors"
          aria-label="Next"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      {/* Right: Legends, View Toggle, Filter, Search, Button */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full lg:w-auto mt-2 sm:mt-0">
        {/* View Toggles */}
        <div
          className="inline-flex items-center p-1 rounded-lg bg-[#F8F9FA] w-full sm:w-auto justify-center"
          role="tablist"
        >
          {VIEWS.map((item) => {
            const active = view === item.value;
            return (
              <button
                key={item.value}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onViewChange(item.value)}
                className={`h-8 sm:h-8 px-3 sm:px-4 rounded-md text-xs sm:text-sm font-medium transition-colors flex-1 sm:flex-none ${
                  active
                    ? 'bg-primary text-white shadow-sm'
                    : 'text-[#6B7280] hover:text-[#1F2241]'
                }`}
              >
                {item.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* Status Filter */}
          <div className="flex-1 sm:flex-none sm:w-[140px]">
            <CustomSelect
              value={statusFilter || ''}
              onChange={onStatusFilterChange}
              options={STATUS_OPTIONS}
              hideCheckIcon
              buttonClassName="w-full h-9 sm:h-10 flex items-center justify-between rounded-lg bg-white border border-[#E5E7EB] px-2 sm:px-3.5 text-xs sm:text-sm font-medium text-[#6B7280] hover:border-[#50AB3D] transition-colors focus:outline-none focus:ring-[2px] focus:ring-[#50AB3D]/20 cursor-pointer"
              renderOption={(option) => {
                const styles = {
                  '': 'bg-[#F4F6F8] text-[#6B7280]',
                  scheduled: 'bg-[#FEF5E7] text-[#F1A132]',
                  completed: 'bg-[#EBF4EA] text-[#50AB3D]',
                  cancelled: 'bg-[#FDECEC] text-[#F34343]',
                };
                const styleClass = styles[option.value] || styles[''];
                return (
                  <div
                    className={`w-full px-3 py-1.5 rounded-md text-xs sm:text-sm text-center font-medium ${styleClass}`}
                  >
                    {option.label}
                  </div>
                );
              }}
            />
          </div>

          {/* Search Input */}
          <div className="flex-1 sm:flex-none sm:w-[180px]">
            <SearchInput
              value={searchQuery || ''}
              onChange={(e) => onSearchQueryChange?.(e.target.value)}
              placeholder="Search..."
              className="w-full"
              inputClassName="h-9 sm:h-10 rounded-lg bg-white border border-[#E5E7EB] focus:border-[#50AB3D] focus:ring-[2px] focus:ring-[#50AB3D]/20 text-xs sm:text-sm"
            />
          </div>
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* New Appointment Button */}
          {canCreate && (
            <button
              type="button"
              onClick={onNewAppointment}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 h-9 sm:h-10 px-3 sm:px-4 rounded-lg bg-primary text-white text-xs sm:text-sm font-medium hover:bg-primary-hover transition-colors"
            >
              <span>New Appointment</span>
              <img
                src={addSquareIcon}
                alt="Add"
                className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              />
            </button>
          )}
          {/* Settings Button */}
          {canViewSettings && (
            <button
              type="button"
              onClick={() => navigate('/calendar/availability')}
              title="Booking availability"
              className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 shrink-0 rounded-lg bg-white border border-[#E5E7EB] text-[#6B7280] hover:border-[#50AB3D] hover:text-[#50AB3D] transition-colors"
            >
              <SettingsIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
